import React, { useEffect, useState, useCallback, useRef, useLayoutEffect } from "react";
import {
  Button,
  UncontrolledTooltip,
  Row,
  Col,
  Nav,
  NavItem,
  NavLink,
  TabContent,
  TabPane,
  Spinner
} from "reactstrap";
import { Link } from "react-router-dom";
import SimpleBar from "simplebar-react";
import classnames from "classnames";
import dayjs from 'dayjs'; // Import dayjs
import remarkGfm from 'remark-gfm';
import { isEmpty, map } from "lodash";
import ReactMarkdown from 'react-markdown';
import PerfectScrollbar from "react-perfect-scrollbar";
import "react-perfect-scrollbar/dist/css/styles.css";

// redux
import { useSelector, useDispatch } from "react-redux";
import {
  getChatSessionHistory,
  getChatHistory,
  editChatSessionTitle,
  deleteChatSession,
  streamAIResponse,
} from "../../../slices/thunks";
import { addMessage, clearMessages } from "../../../slices/AIChat/reducer"; 

import avatar2 from "../../../assets/images/users/avatar-2.jpg";

import { createSelector } from "reselect";
import PersonalInfo from "../../Chat/PersonalInfo";
import { useTranslation } from "react-i18next";

import ThinkingLoader from "../../../Components/Common/AIChat/ThinkingLoader";
import MessageActions from "../../../Components/Common/AIChat/MessageActions";
import SuggestedQuestions from "../../../Components/Common/AIChat/SuggestedQuestions";
import DeleteModal from "../../../Components/Common/DeleteModal";
import MarkdownRenderer from "../../../Components/Common/MarkdownRenderer";
// import MarkdownTypewriterRenderer from "../../../Components/Common/TypewriterEffect";
import { getAuthUser } from "../../../helpers/utils";

const StreamingMessage = ({ messages, aiResponseLoading }) => {
  const lastMessage = messages[messages.length - 1];
  const shouldShow = aiResponseLoading && lastMessage && lastMessage.isStreaming;

  if (!shouldShow) {
    return null;
  }

  return (
    <li className="chat-list left" style={{ marginRight: "15px" }}>
      <div className="conversation-list">
        <div className="user-chat-content" style={{ maxWidth: "80%" }}>
          <div className="ctext-wrap">
            {lastMessage.Message ? (
              <div className="ctext-wrap-content">
                <MarkdownRenderer markdown={lastMessage.Message} />
              </div>
            ) : (
              <ThinkingLoader />
            )}
          </div>
        </div>
      </div>
    </li>
  );
};

const AIChatCases = () => {
  const userChatShow = useRef();
  const { t, i18n } = useTranslation();
  const { user } =  getAuthUser();  
  const [customActiveTab, setcustomActiveTab] = useState("1");

  const toggleCustom = (tab) => {
    if (customActiveTab !== tab) {
      setcustomActiveTab(tab);
    }
  };

  const dispatch = useDispatch();
  const [isInfoDetails, setIsInfoDetails] = useState(false);
  const [Chat_Box_Username, setChat_Box_Username] = useState("Alitqan AI");
  const [Chat_Box_Image, setChat_Box_Image] = useState(avatar2);
  const [currentSessionId, setCurrentSessionId] = useState(null); 
  const scrollbarRef = useRef(null); 
  const justLikedRef = useRef(false);
  const [isFetchingOlderMessages, setIsFetchingOlderMessages] = useState(false);

  const [messageBox, setMessageBox] = useState(null);
  const [curMessage, setcurMessage] = useState("");
  const [search_Menu, setsearch_Menu] = useState(false);
  const [settings_Menu, setsettings_Menu] = useState(false);
  const [editingSessionId, setEditingSessionId] = useState(null);
  const [editingTitle, setEditingTitle] = useState("");
  const [deleteModal, setDeleteModal] = useState(false);
  const [sessionToDelete, setSessionToDelete] = useState(null);
  const scrollHeightBeforeUpdate = useRef(0);

  // New state and ref for enhanced scroll management
  const [showScrollDownButton, setShowScrollDownButton] = useState(false);
  const isAtBottomRef = useRef(true);
  const skipNextScrollToBottom = useRef(false); 

  // Redux Selectors
  const selectAIChatState = (state) => state.AIChat;
  const selectChatState = (state) => state.Chat; 
  const selectAuthState = (state) => state.Login;
 
  const aiChatSelectorProperties = createSelector(
    selectAIChatState,
      (aiChat) => ({
      aiResponseLoading: aiChat.aiResponseLoading, 
      aiResponseError: aiChat.aiResponseError,
      chatSessionHistoryLoading: aiChat.chatSessionHistoryLoading,
      chatSessionHistoryError: aiChat.chatSessionHistoryError,
      chatSessionHistory: aiChat.chatSessionHistory,
      messages: aiChat.messages, 
      messagesLoading: aiChat.messagesLoading, 
      currentPage: aiChat.currentPage,
      totalPages: aiChat.totalPages,
      hasMoreMessages: aiChat.hasMoreMessages,
      messagesError: aiChat.messagesError, 
    })
  );

  const chatScreenSelectorProperties = createSelector(
    selectChatState,
    selectAuthState,
    (chat, auth) => ({
      currentUser: auth.user || { name: "User" }, 
    })
  );

  const {
    aiResponseLoading,
    aiResponseError,
    chatSessionHistoryLoading,
    chatSessionHistoryError,
    chatSessionHistory,
    messages, 
    messagesLoading, 
    currentPage,
    totalPages,
    hasMoreMessages,
    messagesError,
  } = useSelector(aiChatSelectorProperties);
  const { currentUser } = useSelector(chatScreenSelectorProperties);

  const chatInputRef = useRef(null); 

  const startNewChat = () => {
    dispatch(clearMessages()); 
    setCurrentSessionId(null); 
    setChat_Box_Username("Alitqan AI"); 
    setcustomActiveTab("1"); 
  };

  const handleSuggestedQuestionClick = (question) => {
    setcurMessage(question);
    if (chatInputRef.current) {
      chatInputRef.current.focus();
    }
  };


  // Called when the user clicks the "Delete" icon
  const handleDeleteClick = (e, sessionId) => {
    e.preventDefault(); // Prevent navigation
    e.stopPropagation(); // Stop the userChatOpen event from firing
    setSessionToDelete(sessionId);
    setDeleteModal(true);
  };

  const confirmDeleteSession = async () => {
    if (sessionToDelete) {
      try {
        setDeleteModal(false);
        await dispatch(deleteChatSession({ SessionID: sessionToDelete })).unwrap();
        // If the deleted chat was the one currently open, start a new chat.
        if (currentSessionId === sessionToDelete) {
          startNewChat();
        }
      } catch (err) {
        // error is already handled by toast in thunk
      } finally {
        setSessionToDelete(null);
      }
    }
  };

  // Called when the user clicks the "Edit" icon
  const handleEditClick = (e, session) => {
    e.preventDefault();
    e.stopPropagation();
    setEditingSessionId(session.SessionID);
    setEditingTitle(session.Title);
  };

  // Called when the user saves an edit
  const handleSaveEdit = () => {
    if (editingSessionId && editingTitle.trim() !== "") {

      dispatch(editChatSessionTitle({ SessionID: editingSessionId, Title: editingTitle }));
      // Exit editing mode
      setEditingSessionId(null);
      setEditingTitle("");
    }
  };

  // Called when the user cancels an edit (e.g., by pressing Escape)
  const handleCancelEdit = () => {
    setEditingSessionId(null);
    setEditingTitle("");
  };

  // Handle keyboard events on the input field
  const handleInputKeyDown = (e) => {
    if (e.key === "Enter") {
      handleSaveEdit();
    } else if (e.key === "Escape") {
      handleCancelEdit();
    }
  };

  // Automatically focus the input when editing starts
  const editInputRef = (input) => {
    if (input) {
      input.focus();
    }
  };

  const userChatOpen = (sessionId, sessionTitle) => {
    if (sessionId === currentSessionId) return; 

    setCurrentSessionId(sessionId); 
    dispatch(clearMessages()); 
    dispatch(getChatHistory({ SessionID: sessionId, offset: 0, limit: 20 })).then(() => {
      scrollToBottom('auto');
    }); 
    if (userChatShow.current && window.innerWidth < 892) {
      userChatShow.current.classList.add("user-chat-show");
    }
  };


  const backToUserChat = () => {
    userChatShow.current.classList.remove("user-chat-show");
  };


  const handleUserSendMessage = () => {
    if (!curMessage.trim()) return;

    const userMessagePayload = {
      ID: `user-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`, 
      SessionID: currentSessionId,
      Message: curMessage,
      IsReceived: false, 
      CreatedDate: new Date().toISOString(),
    };
    dispatch(addMessage(userMessagePayload));

    isAtBottomRef.current = true;

    dispatch(streamAIResponse({ query: curMessage, SessionID: currentSessionId, IsLegal: false }));
    setcurMessage("");
  };

  // searchUsers function to filter chat sessions
  const searchUsers = () => {
    var input, filter, li, a, i, txtValue;
    input = document.getElementById("search-user");
    filter = input.value.toUpperCase();
    var userList = document.getElementsByClassName("users-list");
    Array.prototype.forEach.call(userList, function (el) {
      li = el.getElementsByTagName("li");
      for (i = 0; i < li.length; i++) {
        a = li[i].getElementsByTagName("a")[0];
        txtValue = a.textContent || a.innerText;
        if (txtValue.toUpperCase().indexOf(filter) > -1) {
          li[i].style.display = "";
        } else {
          li[i].style.display = "none";
        }
      }
    });
  };
  
  // Handle Enter key press in the chat input
  const onKeyPress = (e) => {
    const { key } = e;
    if (key === "Enter") {
      e.preventDefault();
      handleUserSendMessage();
    }
  };
  
  const scrollToBottom = useCallback((behavior = "smooth") => {
    if (messageBox) {
      messageBox.scrollTo({
        top: messageBox.scrollHeight,
        behavior: behavior
      });
      isAtBottomRef.current = true;
      setShowScrollDownButton(false);
    }
  }, [messageBox]);

  const handleScroll = useCallback(() => {
    if (messageBox) {
      const { scrollTop, scrollHeight, clientHeight } = messageBox;
      const scrollThreshold = 100; // Pixels from bottom to be considered "at bottom"

      // Update if user is at the bottom
      isAtBottomRef.current = scrollHeight - scrollTop - clientHeight <= scrollThreshold;

      // Show/hide the scroll-to-bottom button
      setShowScrollDownButton(scrollHeight - scrollTop - clientHeight > scrollThreshold * 2);

      // Logic for fetching older messages when scrolled to the top
      if (scrollTop === 0 && hasMoreMessages && !messagesLoading && currentSessionId !== null) {
        scrollHeightBeforeUpdate.current = scrollHeight;
        setIsFetchingOlderMessages(true);
        const nextPageOffset = currentPage * 20;
        dispatch(getChatHistory({ SessionID: currentSessionId, offset: nextPageOffset, limit: 20 }));
      }
    }
  }, [messageBox, hasMoreMessages, messagesLoading, currentSessionId, currentPage, dispatch]);
  
    // Effect to attach/detach scroll event listener
  useEffect(() => {
    const currentScrollable = messageBox;
    if (currentScrollable) {
      currentScrollable.addEventListener('scroll', handleScroll);
      return () => {
        currentScrollable.removeEventListener('scroll', handleScroll);
      };
    }
  }, [messageBox, handleScroll]);

  // Effect to manage scrolling when messages change
  useLayoutEffect(() => {
    if (!messageBox) return;

    if (isFetchingOlderMessages) {
      // Adjust scroll position after older messages are loaded
      const newScrollHeight = messageBox.scrollHeight;
      messageBox.scrollTop = newScrollHeight - scrollHeightBeforeUpdate.current;
      setIsFetchingOlderMessages(false);
      skipNextScrollToBottom.current = true; // Prevent immediate scroll-to-bottom
      return;
    }

    const lastMessage = messages[messages.length - 1];

    // If the user was at the bottom, scroll down.
    if (isAtBottomRef.current) {
      scrollToBottom("auto");
    }

    skipNextScrollToBottom.current = false; // Reset for next update
  }, [messages, isFetchingOlderMessages, messageBox, scrollToBottom]);

  // Enhanced auto-scroll effect
  useEffect(() => {
    if (!isEmpty(messages) && isAtBottomRef.current && !isFetchingOlderMessages) {
      scrollToBottom("auto"); 
    }
  }, [messages, scrollToBottom, isFetchingOlderMessages]);

  // Fetch chat session history on component mount
  useEffect(() => {
    dispatch(getChatSessionHistory({ IsLegal: false }));
  }, [dispatch]);

  // Start a new chat when the component unmounts
  useEffect(() => {
    return () => {
      startNewChat();
    };
  }, []);

  document.title = "Alitqan | AI Chat";

  return (
    <React.Fragment>
      <div className="page-content">
          <div className="chat-wrapper d-lg-flex gap-1 mx-n4 mt-n4 p-1">

            {/* ====== THE TOKEN REFRESH OVERLAY ====== */}
            {/* {isRefreshingToken && <TokenRefreshBanner show={isRefreshingToken} />} */}

            <div className="chat-leftsidebar minimal-border">
              <div className="px-4 pt-4 mb-3">
                <div className="d-flex align-items-start">
                  <div className="flex-grow-1">
                    <h5 className="mb-4">{t("Chats")}</h5>
                  </div>
                  <div className="flex-shrink-0">
                    <UncontrolledTooltip placement="bottom" target="addcontact">
                      {t("New Chat")}
                    </UncontrolledTooltip>

                    <Button
                      color=""
                      id="addcontact"
                      className="btn btn-soft-success btn-sm material-shadow-none"
                      onClick={startNewChat}
                    >
                      <i className="ri-add-line align-bottom"></i>
                    </Button>
                  </div>
                </div>
                <div className="search-box">
                  <input
                    onKeyUp={searchUsers}
                    id="search-user"
                    type="text"
                    className="form-control bg-light border-light"
                    placeholder={t("Search here")}
                  />
                  <i className="ri-search-2-line search-icon"></i>
                </div>
              </div>

              <Nav tabs className="nav nav-tabs nav-tabs-custom nav-success nav-justified mb-3 p-0">
                <NavItem>
                  <NavLink
                    style={{ cursor: "pointer" }}
                    className={classnames({
                      active: customActiveTab === "1",
                    })}
                    onClick={() => {
                      toggleCustom("1");
                    }}
                  >
                    {t("Chats History")}
                  </NavLink>
                </NavItem>
              </Nav>

               <TabContent
                  activeTab={customActiveTab}
                  className="text-muted"
              >
                  <TabPane tabId="1" id="chats">

                    <SimpleBar
                      className="chat-room-list pt-3"
                      style={{ margin: "-16px 0px 0px" }}
                    >
                      <div className="d-flex align-items-center px-4 mb-2">
                        <div className="flex-grow-1">
                          <h4 className="mb-0 fs-11 text-muted text-uppercase">
                            {t("Chats History")}
                          </h4>
                        </div>
                      </div>

                      <div className="chat-message-list">
                        <ul
                          className="list-unstyled chat-list chat-user-list users-list"
                          id="userList"
                          style={{ paddingRight: "0px" }}
                        >
                          {chatSessionHistory && chatSessionHistory.map((session) => (
                            <li
                              key={session.SessionID}
                              className={
                                currentSessionId === session.SessionID ? "active" : ""
                              }
                            >
                              <a
                                href="#!" 
                                onClick={(e) => {
                                   e.preventDefault(); 
                                  // Don't switch chats if we are in edit mode for another item
                                  if (editingSessionId !== null && editingSessionId !== session.SessionID) return;
                                  // If we click on the item being edited, save it instead of opening
                                  if (editingSessionId === session.SessionID) {
                                    handleSaveEdit();
                                  } else {
                                    userChatOpen(
                                      session.SessionID,
                                      session.Title,
                                    );
                                  }
                                }}
                              >
                                <div className="d-flex align-items-center">

                                  <div className="flex-grow-1 overflow-hidden me-2">
                                    {/* --- Conditional Rendering: Show Input or Title --- */}
                                    {editingSessionId === session.SessionID ? (
                                      <input
                                        ref={editInputRef} // For auto-focus
                                        type="text"
                                        className="edit-title-input"
                                        value={editingTitle}
                                        onChange={(e) => setEditingTitle(e.target.value)}
                                        onKeyDown={handleInputKeyDown}
                                        onBlur={handleSaveEdit} // Save when user clicks away
                                        onClick={(e) => e.stopPropagation()} // Prevent link navigation
                                      />
                                    ) : (
                                      <p className="text-truncate mb-0">{session.Title}</p>
                                    )}
                                  </div>
                                  
                                  {/* --- Edit and Delete Action Icons --- */}
                                  {/* We don't show actions for the item currently being edited */}
                                  {editingSessionId !== session.SessionID && (
                                    <div className="chat-actions">
                                      {/* Using Remix Icon, but you can use any icon library */}
                                      <button onClick={(e) => handleEditClick(e, session)} title="Edit Title">
                                        <i className="ri-pencil-line"></i>
                                      </button>
                                      <button onClick={(e) => handleDeleteClick(e, session.SessionID)} title="Delete Chat">
                                        <i className="ri-delete-bin-line"></i>
                                      </button>
                                    </div>
                                  )}

                                </div>
                              </a>
                            </li>
                          ))}
                        </ul>
                      </div>
                    {chatSessionHistoryLoading && (
                      <div className="d-flex justify-content-center align-items-center py-3">
                        <Spinner color="dark" />
                      </div>
                    )}
                    </SimpleBar>

                  </TabPane>

                  <TabPane tabId="2" id="contacts">
                          <SimpleBar className="chat-room-list pt-3" style={{ margin: "-16px 0px 0px" }}>
                          </SimpleBar>
                  </TabPane>

                </TabContent>
            </div>

            <div className="user-chat w-100 overflow-hidden minimal-border" ref={userChatShow}>
                <div className="chat-content d-lg-flex">
                    <div className="w-100 overflow-hidden position-relative">
                    <div className="position-relative">
                        <div className="p-3 user-chat-topbar">
                        <Row className="align-items-center">
                            <Col sm={4} xs={8}>
                            <div className="d-flex align-items-center">
                                <div className="flex-shrink-0 d-block d-lg-none me-3">
                                <Link to="#" onClick={backToUserChat} className="user-chat-remove fs-18 p-1">
                                    <i className="ri-arrow-left-s-line align-bottom"></i>
                                </Link>
                                </div>
                                <div className="flex-grow-1 overflow-hidden me-1">
                                <div className="d-flex align-items-center">
                                    <div className="flex-shrink-0 chat-user-img align-self-center me-3 ms-0">
                                    <div className="avatar-xs">
                                        <div className="avatar-title rounded-circle bg-success-subtle text-success">
                                        <i className="ri-robot-2-line"></i>
                                        </div>
                                    </div>
                                    </div>
                                    <div className="flex-grow-1 overflow-hidden me-1">
                                    <h5 className="text-truncate mb-0 fs-16">
                                        {t(Chat_Box_Username) || "AI Assistant"}
                                    </h5>
                                    <p className="text-truncate text-muted fs-12 mb-0">
                                        {t("Ask me anything")}
                                    </p>
                                    </div>
                                </div>
                                </div>
                            </div>
                            </Col>
                            <Col sm={8} xs={4}>
                            </Col>
                        </Row>
                        </div>
                        <div className="position-relative" id="users-chat" >
                             {isFetchingOlderMessages && (
                                <div className="d-flex justify-content-center align-items-center py-2">
                                    <Spinner color="dark" size="sm" />
                                </div>
                            )}
                          <div className="chat-conversation p-3 p-lg-4" id="chat-conversation" style={{ position: 'relative' }}>
                            <PerfectScrollbar
                              ref={scrollbarRef} 
                              containerRef={ref => setMessageBox(ref)}
                              className={i18n.dir() === 'rtl' ? 'chat-scrollbar-rtl' : 'chat-scrollbar-ltr'}>
                            {/* This spinner shows at the top ONLY when fetching OLDER messages. */}
                            {isFetchingOlderMessages && (
                                <div className="d-flex justify-content-center align-items-center py-2">
                                    <Spinner color="dark" size="sm" />
                                </div>
                            )}
                            <ul className="list-unstyled chat-conversation-list" id="users-conversation">
                                {messagesLoading && messages.length === 0 ? ( // Only show full loader on initial load
                                    <div className="d-flex justify-content-center align-items-center h-100" style={{ minHeight: '200px' }}>
                                        <Spinner color="dark" />
                                    </div>
                                ) : (
                                    messages && messages.length > 0 ? (
                                        map(messages, (message, key) => {
                                          if (message.isStreaming) {
                                              return null; 
                                          }
                                        return (

                                            <li
                                                className={message.IsReceived === false ? "chat-list right" : "chat-list left"}
                                                key={message.ID}
                                                id={message.ID}
                                                style={{ marginRight: '15px'}}
                                            >
                                              <div className="conversation-list">
                                                    <div className="user-chat-content">
                                                        <div className="ctext-wrap">
                                                            <div className="ctext-wrap-content">
                                                                {message.IsReceived ? (
                                                                  <MarkdownRenderer markdown={message.Message} />
                                                                ) : (
                                                                  <p className="mb-0 ctext-content">
                                                                    {message.Message}
                                                                  </p>
                                                                )}
                                                                {message.IsReceived && message.Suggestions  && (
                                                                    <SuggestedQuestions
                                                                        questions={message.Suggestions}
                                                                        onQuestionClick={handleSuggestedQuestionClick}
                                                                        t={t}
                                                                    />
                                                                )}
                                                            </div>
                                                        </div>
                                                      <div 
                                                          className="message-footer d-flex align-items-center mt-2"
                                                          style={{ width: '100%' }} 
                                                      >
                                                          <small className="text-muted time" dir="ltr" style={{ fontSize: '11px', whiteSpace: 'nowrap' }}>
                                                              {/* {dayjs(message.CreatedAt).format('hh:mm a')} */}
                                                              {new Date(message.CreatedDate).toLocaleString('default', {
                                                                hour: 'numeric',
                                                                minute: '2-digit',
                                                                hour12: true
                                                              })}
                                                          </small>
                                                          <div>
                                                              {message.IsReceived && (
                                                                  <MessageActions message={message} onLikeAction={() => { justLikedRef.current = true; }} />
                                                              )}
                                                          </div>
                                                      </div>

                                                    </div>
                                                </div>
                                            </li>
                                        )})
                                    ) : (
                                        <div className="text-center text-muted">
                                            <i className="ri-robot-2-line fs-1 text-success mb-3"></i>
                                            <p className="fs-5">{t("Start a conversation by typing a message")}</p>
                                        </div>
                                    )
                                )}
                                <StreamingMessage messages={messages} aiResponseLoading={aiResponseLoading} onStreamComplete={scrollToBottom} />
                              </ul>
                            </PerfectScrollbar>

                            {/* ====== SCROLL TO BOTTOM BUTTON ====== */}
                            {/* {showScrollDownButton && (
                              <div
                                className="position-absolute"
                                style={{
                                  bottom: "20px",
                                  right: "25px",
                                  zIndex: 10,
                                }}
                              >
                                <Button 
                                  color="light"
                                  className="btn-icon rounded-circle shadow"
                                  onClick={handleScrollToBottomClick}
                                >
                                  <i className="ri-arrow-down-line"></i>
                                </Button>
                              </div>
                            )} */}

                        </div>
                        </div>
                        
                        <div className="chat-input-section p-3 p-lg-4 border-top">
                        <form id="chatinput-form" onSubmit={(e) => { e.preventDefault(); handleUserSendMessage(); }}>
                            <Row className="g-0 align-items-center">
                                <div className="col-auto">
                                    <div className="chat-input-links me-2">
                                    </div>
                                </div>
                                <div className="col">
                                    <input
                                    ref={chatInputRef}
                                    type="text"
                                    value={curMessage}
                                    onKeyDown={onKeyPress}
                                    onChange={e => setcurMessage(e.target.value)}
                                    className="form-control chat-input bg-light border-light"
                                    id="chat-input"
                                    placeholder={t("Type your message to the AI")}
                                    />
                                </div>
                                <div className="col-auto">
                                    <div className="chat-input-links ms-2 me-2">
                                    <div className="links-list-item">
                                        <Button type="submit" color="success" style={i18n.dir() === 'rtl' ? { transform: 'scaleX(-1)' } : {}} className="chat-send waves-effect waves-light" disabled={aiResponseLoading}>
                                            <i className="ri-send-plane-2-fill align-bottom"></i>
                                        </Button>
                                    </div>
                                    </div>
                                </div>
                            </Row>
                        </form>
                        </div>

                    </div>
                    </div>
                </div>
            </div>
          </div>
      </div>

      <PersonalInfo
        show={isInfoDetails}
        onCloseClick={() => setIsInfoDetails(false)}
        currentuser={Chat_Box_Username}
        cuurentiseImg={Chat_Box_Image}
      />

      <DeleteModal
        showDelete={deleteModal}
        setShowDelete={setDeleteModal}
        onDeleteClick={confirmDeleteSession}
        description={t("Are you sure you want to delete this chat")}
      />
    </React.Fragment>
  );
};

export default AIChatCases;